import { useEffect, useState } from "react";
import { BellRing, BellOff, Send, Repeat, Megaphone, UtensilsCrossed, Moon, Clock, Globe, Loader2, Award, Mail, Sun, MessageSquare, Cake } from "lucide-react";
import { BrutalButton } from "@/components/Brutal";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { normalizePhone } from "@/lib/phone";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";

const TRIGGERS = [
  { key: "assigned", label: "Taak aan mij toegewezen", icon: Send },
  { key: "reminders", label: "Taak-herinneringen (DUE)", icon: Repeat },
  { key: "digest", label: "Dagelijkse samenvatting ('s ochtends)", icon: Sun },
  { key: "announcements", label: "Nieuwe mededelingen", icon: Megaphone },
  { key: "polls", label: "Nieuwe polls", icon: UtensilsCrossed },
  { key: "badges", label: "Nieuwe badges & mijlpalen", icon: Award },
  { key: "birthdays", label: "Verjaardagen in huis 🎉", icon: Cake },
];

const CHANNELS = [
  { key: "default", label: "Standaard" },
  { key: "push", label: "Push" },
  { key: "email", label: "E-mail" },
  { key: "sms", label: "SMS" },
];

const TIMEZONES = [
  "Europe/Amsterdam", "Europe/Brussels", "Europe/Berlin", "Europe/Paris",
  "Europe/Madrid", "Europe/London", "UTC",
];

export default function NotifPrefsDialog({ open, onClose, pushOn, onTogglePush, pushSupported = true, pushBusy = false }) {
  const t = useT();
  const { user } = useAuth();
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) api.get("/notif-prefs").then((r) => setPrefs(r.data)).catch(() => setPrefs(null));
  }, [open]);

  const set = (k, v) => setPrefs((p) => ({ ...p, [k]: v }));

  const toggleChannel = (key) => setPrefs((p) => {
    const cur = p.primary_channels || (p.primary_channel ? [p.primary_channel] : ["push"]);
    let next = cur.includes(key) ? cur.filter((c) => c !== key) : [...cur, key];
    if (next.length === 0) next = [key];
    return { ...p, primary_channels: next, primary_channel: next[0] };
  });

  const toggleOverride = (key, ch) => setPrefs((p) => {
    const ov = { ...(p.channel_overrides || {}) };
    const raw = ov[key];
    const cur = Array.isArray(raw) ? raw : (typeof raw === "string" && raw ? [raw] : []);
    const next = cur.includes(ch) ? cur.filter((c) => c !== ch) : [...cur, ch];
    if (next.length === 0) delete ov[key]; else ov[key] = next;
    return { ...p, channel_overrides: ov };
  });

  const clearOverride = (key) => setPrefs((p) => {
    const ov = { ...(p.channel_overrides || {}) };
    delete ov[key];
    return { ...p, channel_overrides: ov };
  });

  const channelStatus = (ch) => {
    if (ch === "push") {
      if (!pushSupported) return { ok: false, msg: "Push wordt niet ondersteund op dit apparaat" };
      if (!pushOn) return { ok: false, msg: "Push staat uit — zet 'm hierboven aan" };
      return { ok: true, msg: "Klaar om te bezorgen via push" };
    }
    if (ch === "email") {
      return user?.email ? { ok: true, msg: `Klaar om te bezorgen op ${user.email}` } : { ok: false, msg: "Geen e-mailadres bekend" };
    }
    // sms
    return user?.phone
      ? { ok: true, msg: `Klaar om te bezorgen op ${normalizePhone(user.phone)}` }
      : { ok: false, msg: "Geen telefoonnummer — vul het in bij je profiel, anders komen meldingen niet aan" };
  };

  const save = async () => {
    setSaving(true);
    try { await api.put("/notif-prefs", prefs); toast.success("Voorkeuren opgeslagen"); onClose(); }
    catch { toast.error("Opslaan mislukt"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl max-h-[90vh] overflow-auto no-scrollbar" data-testid="notif-prefs-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-head font-black text-xl flex items-center gap-2"><BellRing className="w-5 h-5" strokeWidth={2.5} />Meldingsvoorkeuren</DialogTitle></DialogHeader>
        {!prefs ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
          <div className="space-y-4">
            {onTogglePush && (
              <button data-testid="toggle-push-btn" onClick={onTogglePush} disabled={pushBusy || !pushSupported}
                className={`w-full brutal-sm rounded-xl p-4 flex items-center gap-3 disabled:opacity-50 ${pushOn ? "bg-brand-green" : "bg-brand-yellow"}`}>
                {pushOn ? <BellRing className="w-5 h-5 shrink-0" strokeWidth={2.5} /> : <BellOff className="w-5 h-5 shrink-0" strokeWidth={2.5} />}
                <div className="flex-1 text-left">
                  <p className="font-head font-bold">Push-meldingen op dit apparaat</p>
                  <p className="text-xs text-brand-ink/60">{!pushSupported ? "Niet ondersteund op dit apparaat" : pushOn ? "Aan — je krijgt meldingen" : "Uit — tik om aan te zetten"}</p>
                </div>
                <span className={`w-11 h-6 rounded-full border-2 border-brand-ink relative shrink-0 ${pushOn ? "bg-white" : "bg-white/60"}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-brand-ink transition-all ${pushOn ? "left-5" : "left-0.5"}`} />
                </span>
              </button>
            )}
            <div className="brutal-sm rounded-xl p-4 bg-brand-violet/10 space-y-3">
              <p className="font-head font-bold text-sm flex items-center gap-2"><BellRing className="w-4 h-4" strokeWidth={2.5} />Hoofdkanaal / kanalen</p>
              <p className="text-xs text-brand-ink/60 font-semibold -mt-1">Kies één of meer kanalen. Je ontvangt elke melding op álle gekozen kanalen (bijv. push én e-mail).</p>
              {(() => {
                const channels = prefs.primary_channels || (prefs.primary_channel ? [prefs.primary_channel] : ["push"]);
                return (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { key: "push", label: "Push", icon: BellRing },
                        { key: "email", label: "E-mail", icon: Mail },
                        { key: "sms", label: "SMS", icon: MessageSquare },
                      ].map((c) => {
                        const active = channels.includes(c.key);
                        return (
                          <button key={c.key} data-testid={`channel-${c.key}`} onClick={() => toggleChannel(c.key)}
                            className={`brutal-sm brutal-press rounded-xl px-3 py-3 flex items-center gap-2 font-bold text-sm ${active ? "bg-brand-violet text-white" : "bg-white"}`}>
                            <span className={`w-4 h-4 rounded-md border-2 flex items-center justify-center shrink-0 ${active ? "bg-white border-white" : "border-brand-ink/30"}`}>{active && <span className="w-2 h-2 rounded-sm bg-brand-violet" />}</span>
                            <c.icon className="w-4 h-4 shrink-0" strokeWidth={2.5} />{c.label}
                          </button>
                        );
                      })}
                    </div>
                    <div className="space-y-1.5">
                      {channels.map((ch) => {
                        const st = channelStatus(ch);
                        const lbl = { push: "Push", email: "E-mail", sms: "SMS" }[ch] || ch;
                        return (
                          <div key={ch} data-testid={`channel-status-${ch}`} className={`rounded-xl px-3 py-2 text-xs font-bold flex items-start gap-2 ${st.ok ? "bg-brand-green/20 text-green-800" : "bg-amber-100 text-amber-800"}`}>
                            <span className="shrink-0">{st.ok ? "✓" : "⚠️"}</span><span><b>{lbl}:</b> {t(st.msg)}</span>
                          </div>
                        );
                      })}
                    </div>
                    {channels.includes("sms") && (
                <div className="space-y-2">
                  <p className="text-xs text-brand-ink/70 font-semibold leading-snug">
                    Zorg dat je telefoonnummer bij je profiel staat (internationaal, bv. +316…). Standaard SMS-tarief kan van toepassing zijn.
                  </p>
                  <button data-testid="test-sms-btn" onClick={async () => {
                    try {
                      await api.post("/sms/test");
                      toast.success("Test-SMS verstuurd — check je telefoon 📱");
                    } catch (e) { toast.error(e.response?.data?.detail || "Versturen mislukt"); }
                  }}
                    className="w-full brutal-sm brutal-press rounded-xl px-3 py-2 bg-white text-sm font-bold flex items-center justify-center gap-2">
                    <MessageSquare className="w-4 h-4" strokeWidth={2.5} />Stuur test-SMS
                  </button>
                </div>
              )}
                  </>
                );
              })()}
            </div>

            <div className="space-y-2">
              <p className="font-head font-bold text-sm text-brand-ink/60">Waarvoor wil je een melding? Kies per soort — en optioneel een eigen kanaal.</p>
              {TRIGGERS.map((tr) => (
                <div key={tr.key} className="w-full brutal-sm rounded-xl px-4 py-3 bg-white space-y-2">
                  <div className="flex items-center gap-3 font-bold text-sm">
                    <tr.icon className="w-4 h-4 shrink-0" strokeWidth={2.5} />
                    <span className="flex-1 text-left">{tr.label}</span>
                    <button data-testid={`pref-${tr.key}`} onClick={() => set(tr.key, !prefs[tr.key])}
                      className={`w-11 h-6 rounded-full border-2 border-brand-ink relative transition-colors shrink-0 ${prefs[tr.key] ? "bg-brand-green" : "bg-white"}`}>
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-brand-ink transition-all ${prefs[tr.key] ? "left-5" : "left-0.5"}`} />
                    </button>
                  </div>
                  {prefs[tr.key] && (() => {
                    const raw = (prefs.channel_overrides || {})[tr.key];
                    const ovList = Array.isArray(raw) ? raw : (typeof raw === "string" && raw ? [raw] : []);
                    const isDefault = ovList.length === 0;
                    const CH = { push: { label: "Push", Icon: BellRing }, email: { label: "E-mail", Icon: Mail }, sms: { label: "SMS", Icon: MessageSquare } };
                    const opts = ["push", "email", "sms"];
                    return (
                      <div className="pl-7 space-y-1">
                        <p className="text-xs text-brand-ink/50 font-semibold">{t("Via — kies één of meer kanalen:")}</p>
                        <div className="flex flex-wrap gap-1.5" data-testid={`channel-override-${tr.key}`}>
                          <button data-testid={`channel-override-${tr.key}-default`} onClick={() => clearOverride(tr.key)}
                            className={`inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2.5 py-1 border transition-colors ${isDefault ? "bg-brand-violet text-white border-brand-violet" : "bg-white border-brand-ink/20"}`}>
                            {t("Standaard")}{isDefault ? " ✓" : ""}
                          </button>
                          {opts.map((c) => {
                            const active = ovList.includes(c);
                            const { Icon, label } = CH[c];
                            return (
                              <button key={c} data-testid={`channel-override-${tr.key}-${c}`} onClick={() => toggleOverride(tr.key, c)}
                                className={`inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2.5 py-1 border transition-colors ${active ? "bg-brand-violet text-white border-brand-violet" : "bg-white border-brand-ink/20"}`}>
                                <Icon className="w-3 h-3" strokeWidth={2.5} />{label}{active ? " ✓" : ""}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>

            <div className="brutal-sm rounded-xl p-4 bg-brand-cream space-y-3">
              <button data-testid="pref-quiet_enabled" onClick={() => set("quiet_enabled", !prefs.quiet_enabled)} className="w-full flex items-center gap-3 font-bold text-sm">
                <Moon className="w-4 h-4" strokeWidth={2.5} />
                <span className="flex-1 text-left">Stille uren / nachtstand</span>
                <span className={`w-11 h-6 rounded-full border-2 border-brand-ink relative ${prefs.quiet_enabled ? "bg-brand-blue" : "bg-white"}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-brand-ink transition-all ${prefs.quiet_enabled ? "left-5" : "left-0.5"}`} />
                </span>
              </button>
              {prefs.quiet_enabled && (
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" strokeWidth={2.5} />
                  <input data-testid="pref-quiet-start" type="time" value={prefs.quiet_start} onChange={(e) => set("quiet_start", e.target.value)} className="brutal-sm rounded-lg px-2 py-1.5 font-medium outline-none flex-1 bg-white" />
                  <span className="font-bold">tot</span>
                  <input data-testid="pref-quiet-end" type="time" value={prefs.quiet_end} onChange={(e) => set("quiet_end", e.target.value)} className="brutal-sm rounded-lg px-2 py-1.5 font-medium outline-none flex-1 bg-white" />
                </div>
              )}
            </div>

            <div>
              <p className="font-head font-bold text-sm flex items-center gap-1.5 mb-1"><Globe className="w-4 h-4" strokeWidth={2.5} />Tijdzone (voor herinneringen)</p>
              <select data-testid="pref-timezone" value={prefs.timezone} onChange={(e) => set("timezone", e.target.value)} className="w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none bg-white">
                {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </div>

            <BrutalButton data-testid="save-prefs-btn" onClick={save} disabled={saving} className="w-full">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Opslaan
            </BrutalButton>
            <button data-testid="test-push-btn" onClick={async () => {
              try {
                const { data } = await api.post("/push/test");
                toast.success(data?.email_sent
                  ? "Test verstuurd — check je meldingen én je e-mail 📬"
                  : "Test-melding verstuurd — check je meldingen 🔔");
              } catch { toast.error("Zet eerst meldingen aan"); }
            }}
              className="w-full text-center text-sm font-bold underline decoration-2 decoration-brand-blue">
              Stuur een test-melding
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
